import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Drill, LogOut, Upload, Users as UsersIcon, RefreshCw, Bell, BookOpen, ChevronDown } from "lucide-react";
import ContactsTable from "@/components/ContactsTable";
import FiltersPanel from "@/components/FiltersPanel";
import ImportWizard from "@/components/ImportWizard";
import ContactSheet from "@/components/ContactSheet";
import UsersAdmin from "@/components/UsersAdmin";
import NotificationsAdmin from "@/components/NotificationsAdmin";
import StatsBar from "@/components/StatsBar";
import BulletinBoard from "@/components/BulletinBoard";
import MyFollowups from "@/components/MyFollowups";
import TeamChat from "@/components/TeamChat";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { CalendarClock, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_FILTERS = {
  licensed_state: "all",
  mailing_state: "all",
  license_category: "all",
  status: "all",
  expires_window: "all",
  source: "all",
  search: "",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("all");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [activeContact, setActiveContact] = useState(null);

  const isAdmin = user?.role === "admin";

  const queryParams = useMemo(() => {
    const p = {};
    if (filters.licensed_state !== "all") p.licensed_state = filters.licensed_state;
    if (filters.mailing_state !== "all") p.mailing_state = filters.mailing_state;
    if (filters.license_category !== "all") p.license_category = filters.license_category;
    if (filters.status !== "all") p.status = filters.status;
    if (filters.expires_window !== "all") p.expires_within_days = filters.expires_window;
    if (filters.source !== "all") p.source = filters.source;
    if (filters.search.trim()) p.search = filters.search.trim();
    if (tab === "mine") p.assigned_to_me = true;
    return p;
  }, [filters, tab]);

  const statsParams = useMemo(() => {
    // Stats respect filters EXCEPT status (so buckets remain meaningful)
    const p = { ...queryParams };
    delete p.status;
    return p;
  }, [queryParams]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cs }, { data: st }] = await Promise.all([
        api.get("/contacts", { params: queryParams }),
        api.get("/stats", { params: statsParams }),
      ]);
      setContacts(cs);
      setStats(st);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [queryParams, statsParams]);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch { /* non-admin or network blip */ }
  }, []);

  const [chatUnread, setChatUnread] = useState(0);
  const loadChatUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/threads/unread/count");
      setChatUnread(data?.count || 0);
    } catch { /* network blip - keep current count */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => {
    loadChatUnread();
    const t = setInterval(loadChatUnread, 30000);
    return () => clearInterval(t);
  }, [loadChatUnread]);

  // Refresh whenever the tab regains focus so a stale table (e.g. a teammate just claimed a row)
  // is caught before the user clicks a now-invalid Claim/Contacted button. Debounced to coalesce
  // the visibilitychange + window focus events that some browsers fire back-to-back.
  useEffect(() => {
    let t = null;
    const schedule = () => {
      if (document.visibilityState !== "visible") return;
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; loadAll(); }, 200);
    };
    document.addEventListener("visibilitychange", schedule);
    window.addEventListener("focus", schedule);
    return () => {
      if (t) clearTimeout(t);
      document.removeEventListener("visibilitychange", schedule);
      window.removeEventListener("focus", schedule);
    };
  }, [loadAll]);

  const handleClaim = async (id) => {
    try {
      await api.post(`/contacts/${id}/claim`);
      toast.success("Contact claimed");
      setActiveContact(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const handleContacted = async (id) => {
    try {
      await api.post(`/contacts/${id}/contacted`);
      toast.success("Marked as contacted (record locked)");
      setActiveContact(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };
  const handleReassign = async (id, userId) => {
    try {
      await api.post(`/contacts/${id}/reassign`, { user_id: userId });
      toast.success("Contact reassigned");
      setActiveContact(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="dashboard-page">
      <header className="border-b border-stone-300 bg-white/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center bg-[#1C1917] text-white">
              <Drill className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold tracking-tight text-lg">JUBILEE BOARD</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone-500">
                Board Outreach Console
              </div>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-3">
            {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center h-9 px-3 rounded-sm text-sm font-medium text-white bg-[#B85C38] hover:bg-[#9A4D2E] transition-colors"
                  title="Download QA modules (plain language, 5-minute walkthroughs)"
                  data-testid="qa-modules-menu"
                >
                  <BookOpen className="h-4 w-4 mr-1.5" />
                  QA Modules
                  <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-80" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-[#B85C38]">
                  Post-deploy sanity sweep
                </DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <a
                    href="/downloads/QA_Post_Deploy_Sweep.docx"
                    download="QA_Post_Deploy_Sweep.docx"
                    className="cursor-pointer"
                    data-testid="qa-sweep-download"
                  >
                    <span className="font-medium">Post-Deploy Sweep</span>
                    <span className="ml-1.5 text-stone-500">— 10 min, 6 checks</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-[#B85C38]">
                  Hotfix verification (do this first)
                </DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <a
                    href="/downloads/QA_Hotfix_Claim_Lock.docx"
                    download="QA_Hotfix_Claim_Lock.docx"
                    className="cursor-pointer"
                    data-testid="qa-hotfix-download"
                  >
                    <span className="font-medium">Hotfix QA</span>
                    <span className="ml-1 text-stone-500">— claim/lock (1 tester, 7 min)</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                  Plain-language QA — send one at a time
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a
                    href="/downloads/QA_Module_1_Dashboard_Tour.docx"
                    download="QA_Module_1_Dashboard_Tour.docx"
                    className="cursor-pointer"
                    data-testid="qa-module1-download"
                  >
                    <span className="font-medium">Module 1</span>
                    <span className="ml-1 text-stone-500">— Dashboard tour</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href="/downloads/QA_Module_2_Working_a_Contact.docx"
                    download="QA_Module_2_Working_a_Contact.docx"
                    className="cursor-pointer"
                    data-testid="qa-module2-download"
                  >
                    <span className="font-medium">Module 2</span>
                    <span className="ml-1 text-stone-500">— Working a contact</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href="/downloads/QA_Module_3_Team_Communication.docx"
                    download="QA_Module_3_Team_Communication.docx"
                    className="cursor-pointer"
                    data-testid="qa-module3-download"
                  >
                    <span className="font-medium">Module 3</span>
                    <span className="ml-1 text-stone-500">— Team communication</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a
                    href="/downloads/QA_Module_4_Enrich_Contact.docx"
                    download="QA_Module_4_Enrich_Contact.docx"
                    className="cursor-pointer"
                    data-testid="qa-module4-download"
                  >
                    <span className="font-medium">Module 4</span>
                    <span className="ml-1 text-stone-500">— Fixing wrong info</span>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a
                    href="/downloads/Jubilee_Board_QA_Guide_Plain.docx"
                    download="Jubilee_Board_QA_Guide_Plain.docx"
                    className="cursor-pointer text-stone-600"
                    data-testid="qa-guide-plain-download"
                  >
                    <span>Full combined guide</span>
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            )}
            <Button
              variant="ghost" size="sm" onClick={loadAll}
              className="rounded-sm text-stone-600 hover:text-stone-900"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            {isAdmin && (
              <Button
                onClick={() => setImportOpen(true)}
                size="sm"
                className="rounded-sm bg-[#1C1917] hover:bg-[#292524] text-white"
                data-testid="open-import-btn"
              >
                <Upload className="h-4 w-4 mr-1.5" /> Import Roster
              </Button>
            )}
            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-stone-300">
              <div className="text-right leading-tight">
                <div className="text-sm font-medium" data-testid="current-user-name">{user?.name}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-stone-500">
                  {user?.role}
                </div>
              </div>
              <Button
                variant="outline" size="sm" onClick={logout}
                className="rounded-sm border-stone-300"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 space-y-6">
        <BulletinBoard currentUser={user} />
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-stone-500 mb-3">
              / outreach overview
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-stone-900">
              {tab === "mine"
                ? "Your assigned contacts"
                : tab === "followups"
                ? "Your follow-ups (Snap Count)"
                : tab === "chat"
                ? "Team chat"
                : tab === "users"
                ? "Board members"
                : tab === "notifications"
                ? "Notifications"
                : "All contacts"}
            </h1>
            <p className="mt-2 text-sm text-stone-600 max-w-2xl">
              {tab === "followups"
                ? "Reminders you've set on contacts. Open a contact to add a note or mark it contacted. Dismiss a reminder once you've followed up."
                : tab === "chat"
                ? "Short threads for anything that isn't tied to a specific contact. Everyone can post and reply."
                : "Claim a contact to lock it to your name — only you can call this person once claimed. Mark it contacted after you've reached out — that permanently freezes the record so nobody double-calls them."}
            </p>
          </div>
          {tab !== "chat" && tab !== "users" && tab !== "notifications" && <StatsBar stats={stats} />}
        </div>

        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "chat" || tab === "chat") loadChatUnread(); }} className="w-full">
          <TabsList className="bg-transparent p-0 gap-2 border-b border-stone-300 w-full justify-start rounded-none h-auto">
            <TabsTrigger
              value="all"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B85C38] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-display font-semibold"
              data-testid="tab-all"
            >
              All Contacts
            </TabsTrigger>
            <TabsTrigger
              value="mine"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B85C38] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-display font-semibold"
              data-testid="tab-mine"
            >
              My Assigned
            </TabsTrigger>
            <TabsTrigger
              value="followups"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B85C38] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-display font-semibold"
              data-testid="tab-followups"
            >
              <CalendarClock className="h-4 w-4 mr-1.5" /> Follow-ups
            </TabsTrigger>
            <TabsTrigger
              value="chat"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B85C38] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-display font-semibold relative"
              data-testid="tab-chat"
            >
              <MessageSquare className="h-4 w-4 mr-1.5" /> Team Chat
              {chatUnread > 0 && (
                <span
                  className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-mono px-1.5 rounded-full bg-[#B85C38] text-white"
                  data-testid="chat-unread-badge"
                >
                  {chatUnread}
                </span>
              )}
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger
                value="users"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B85C38] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-display font-semibold"
                data-testid="tab-users"
              >
                <UsersIcon className="h-4 w-4 mr-1.5" /> Board Members
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger
                value="notifications"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B85C38] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 font-display font-semibold"
                data-testid="tab-notifications"
              >
                <Bell className="h-4 w-4 mr-1.5" /> Notifications
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-1">
                <FiltersPanel filters={filters} setFilters={setFilters} resetTo={DEFAULT_FILTERS} />
              </div>
              <div className="lg:col-span-4">
                <ContactsTable
                  contacts={contacts} loading={loading} currentUser={user}
                  onClaim={handleClaim} onContacted={handleContacted}
                  onOpen={(c) => setActiveContact(c)}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="mine" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-1">
                <FiltersPanel filters={filters} setFilters={setFilters} resetTo={DEFAULT_FILTERS} />
              </div>
              <div className="lg:col-span-4">
                <ContactsTable
                  contacts={contacts} loading={loading} currentUser={user}
                  onClaim={handleClaim} onContacted={handleContacted}
                  onOpen={(c) => setActiveContact(c)}
                  emptyTitle="No contacts assigned to you yet"
                  emptyHint="Claim contacts from the All Contacts tab to start your outreach list."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="followups" className="mt-6">
            <MyFollowups onOpenContact={(c) => setActiveContact(c)} />
          </TabsContent>

          <TabsContent value="chat" className="mt-6">
            <TeamChat currentUser={user} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="users" className="mt-6">
              <UsersAdmin users={users} reload={loadUsers} currentUser={user} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="notifications" className="mt-6">
              <NotificationsAdmin />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {importOpen && (
        <ImportWizard
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={loadAll}
        />
      )}

      {activeContact && (
        <ContactSheet
          contact={activeContact}
          users={users}
          currentUser={user}
          onClose={() => setActiveContact(null)}
          onClaim={handleClaim}
          onContacted={handleContacted}
          onReassign={handleReassign}
          onUpdated={loadAll}
        />
      )}
    </div>
  );
}
